import { RadioGroup, RadioGroupItem } from "ui/components/radio-group";
import { JOB_TYPES } from "@/lib/constants";
import { Button } from "ui/components/button";
import SelectStatus from "components/select/status";
import { Label } from "ui/components/label";
import useTableFilters from "@/hooks/useTableFilters";
import { Switch } from "ui/components/switch";
import UserCombobox from "components/directus/combobox/user";
import CalendarDatePicker from "ui/components/comp-507";
import JobCombobox from "components/directus/combobox/job";
import ProjectCombobox from "components/directus/combobox/project";
import { formatDateForAPI } from "utils/utils";
import { EMPLOYEE_FILTERS } from "components/employee/constants";
import TypeFilter from "components/job/type/filter";

export default function BillableFilters({
  filters,
  setFilters,
  setIsOpen,
  isOpen,
  clearFilters,
  defaultFilters,
}) {
  const {
    localFilters,
    applyFilters,
    clearLocalFilters,
    handleSetLocalFilters,
    setLocalFilters,
  } = useTableFilters({
    filters,
    setFilters,
    setIsOpen,
    clearFilters,
    initialFilters: {
      coordinator: undefined,
      status: undefined,
      project: undefined,
      job: undefined,
      billing_date: undefined,
      due_date: undefined,
      ...defaultFilters,
    },
  });

  return (
    <div className="w-full px-4 space-y-4">
      {/* Projects */}
      <div>
        <Label className="mb-2 text-sm">Project</Label>
        <ProjectCombobox
          value={localFilters.job?.project?._eq}
          className="w-full"
          placeholder="Select"
          hideLabel
          searchPlaceholder="Search Project..."
          dependsOn={isOpen}
          onChange={(selectedItem) =>
            handleSetLocalFilters({
              job: {
                ...localFilters?.job,
                project: { ...localFilters.job?.project, _eq: selectedItem },
              },
            })
          }
        />
      </div>

      {/* Jobs */}
      <div>
        <Label className="mb-2 text-sm">Job</Label>
        <JobCombobox
          value={localFilters.job?._eq}
          className="w-full"
          placeholder="Select"
          searchPlaceholder="Search Job..."
          hideLabel
          dependsOn={isOpen}
          onChange={(selectedItem) =>
            handleSetLocalFilters({
              ...localFilters,
              job: {
                ...localFilters.job,
                _eq: selectedItem,
              },
            })
          }
        />
      </div>

      {/* Account Manager */}
      <div>
        <Label className="mb-2 text-sm">Account Manager</Label>
        <UserCombobox
          value={localFilters.job?.project?.account_manager?._eq}
          onChange={(selectedItem) =>
            setLocalFilters({
              job: {
                project: {
                  ...localFilters.job?.project,
                  account_manager: {
                    _eq: selectedItem,
                  },
                },
              },
            })
          }
          filter={EMPLOYEE_FILTERS.account_manager}
          dependenton={isOpen}
        />
      </div>

      {/* Sales Coordinator */}
      <div>
        <Label className="mb-2 text-sm">Sales Coordinator</Label>
        <UserCombobox
          value={localFilters.job?.project?.sales_coordinator?._eq}
          onChange={(selectedItem) =>
            setLocalFilters({
              job: {
                project: {
                  ...localFilters.job?.project,
                  sales_coordinator: {
                    _eq: selectedItem,
                  },
                },
              },
            })
          }
          filter={EMPLOYEE_FILTERS.sales_coordinating}
          dependenton={isOpen}
        />
      </div>

      {/* Type Filter */}
      <div>
        <Label className="mb-2 text-sm">Type</Label>

        <TypeFilter
          value={localFilters?.type?._eq}
          onChange={(value) =>
            handleSetLocalFilters({ type: value ? { _eq: value } : undefined })
          }
        />
      </div>

      {/* Status Filter */}
      <div>
        <Label className="mb-2 text-sm">Status</Label>
        <SelectStatus
          collection="billables"
          value={localFilters?.status?._eq}
          onChange={(selectedItem) => {
            handleSetLocalFilters({
              status: { _eq: selectedItem },
            });
          }}
        />
      </div>

      {/* Billing Date */}
      <div>
        <Label className="mb-2 text-sm">Billing Date</Label>
        <CalendarDatePicker
          stack={true}
          date={{
            from: localFilters?.billing_date?._gte,
            to: localFilters?.billing_date?._lte,
          }}
          setDate={(range) => {
            handleSetLocalFilters({
              billing_date: {
                _gte: range?.from ? formatDateForAPI(range.from) : undefined,
                _lte: range?.to ? formatDateForAPI(range.to) : undefined,
              },
            });
          }}
          className="w-full"
        />
      </div>

      {/* Due Date */}
      <div>
        <Label className="mb-2 text-sm">Due Date</Label>
        <CalendarDatePicker
          stack={true}
          date={{
            from: localFilters?.due_date?._gte,
            to: localFilters?.due_date?._lte,
          }}
          setDate={(range) => {
            handleSetLocalFilters({
              due_date: {
                _gte: range?.from ? formatDateForAPI(range.from) : undefined,
                _lte: range?.to ? formatDateForAPI(range.to) : undefined,
              },
            });
          }}
          className="w-full"
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between ">
          <Label className="text-sm">Delayed Billables</Label>

          <Switch
            checked={
              localFilters?.status?._eq === "contracted" &&
              Boolean(localFilters?.billing_date?._lt)
            }
            onCheckedChange={(checked) => {
              if (checked) {
                const today = formatDateForAPI(new Date());
                handleSetLocalFilters({
                  status: { _eq: "contracted" },
                  billing_date: { _lt: today },
                  // ensure mutual exclusivity
                  due_date: undefined,
                });
              } else {
                handleSetLocalFilters({
                  // only clear if we previously set for delayed
                  billing_date: undefined,
                  ...(localFilters?.status?._eq === "contracted"
                    ? { status: undefined }
                    : {}),
                });
              }
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          {/* <div className="flex flex-col"> */}
          <Label className="text-sm">Overdue Billables</Label>
          {/* <span className="text-xs text-muted-foreground">
              due date passed, still billed
            </span>
          </div> */}
          <Switch
            checked={
              localFilters?.status?._eq === "billed" &&
              Boolean(localFilters?.due_date?._lt)
            }
            onCheckedChange={(checked) => {
              if (checked) {
                const today = formatDateForAPI(new Date());
                handleSetLocalFilters({
                  status: { _eq: "billed" },
                  due_date: { _lt: today },
                  // ensure mutual exclusivity
                  billing_date: undefined,
                });
              } else {
                handleSetLocalFilters({
                  // only clear if we previously set for overdue
                  due_date: undefined,
                  ...(localFilters?.status?._eq === "billed"
                    ? { status: undefined }
                    : {}),
                });
              }
            }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex place-self-end gap-4">
        <Button variant="outline" onClick={clearLocalFilters}>
          Clear
        </Button>
        <Button onClick={() => applyFilters()}>Apply</Button>
      </div>
    </div>
  );
}
